"use client";

import React from "react";
import { Clock, MapPin, Phone, ShieldCheck, Wrench, CircleCheck as CheckCircle2, Navigation, Sparkles } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const coverageZones = [{
  name: "Downtown Jonesboro",
  detail: "Main Street, Courthouse Square & surrounding historic districts",
  zip: "30236"
}, {
  name: "Tara Boulevard Corridor",
  detail: "Commercial strips, residential subdivisions & retail zones",
  zip: "30236 / 30238"
}, {
  name: "Lake Spivey Vicinity",
  detail: "Residential waterfront homes, estates & nearby communities",
  zip: "30236"
}, {
  name: "North Jonesboro & Irondale",
  detail: "Subdivisions along GA-138 and North Main Street",
  zip: "30238"
}, {
  name: "South Clayton & Irondale Park",
  detail: "Local neighborhoods, townhomes & multi-family units",
  zip: "30237"
}, {
  name: "Flippen & Upper River Roads",
  detail: "Fast access routes across eastern Jonesboro borders",
  zip: "30236"
}];
const dispatchHighlights = [{
  icon: Clock,
  title: "30-45 Minute Arrival",
  description: "Fully stocked emergency plumbing vans stationed across Clayton County for fast local response."
}, {
  icon: ShieldCheck,
  title: "Licensed & Insured",
  description: "Georgia state-certified master plumbers equipped to handle catastrophic residential and commercial leaks."
}, {
  icon: Wrench,
  title: "Upfront Transparent Rates",
  description: "No surprise midnight dispatch fees. Clear diagnostic pricing before any plumbing work starts."
}];
export default function ServiceArea() {
  const shouldReduceMotion = useReducedMotion();
  const fadeInVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="serviceareajonesboro" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeInVariants} className="max-w-3xl mx-auto text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 mb-3">
            <Badge variant="secondary" className="px-3 py-1 text-xs font-semibold uppercase tracking-wider">
              <MapPin className="size-3 text-accent" />
              Clayton County Headquarters
            </Badge>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight">
            Jonesboro, GA Emergency Plumbing Coverage
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            From burst pipes in historic Downtown Jonesboro to urgent water heater repairs along Tara Boulevard, our local technicians are ready 24 hours a day, 7 days a week.
          </p>
        </motion.div>

        {/* Main 2-Column Section */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start mb-16">
          {/* Left Column: Visual Map & Response Overview */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInVariants} className="lg:col-span-6 flex flex-col gap-6">
            <div className="relative rounded-2xl overflow-hidden border border-border bg-muted shadow-md group">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/jonesboro_service_area_map.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/jonesboro_service_area_map.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/jonesboro_service_area_map.png-wvc-sizes" alt="Jonesboro Georgia Emergency Plumbing Service Area Coverage" loading="lazy" decoding="async" className="w-full h-80 sm:h-96 object-cover transition-transform duration-500 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/90 via-primary/40 to-transparent flex flex-col justify-end p-6 sm:p-8 text-primary-foreground">
                <div className="flex items-center gap-2 mb-2">
                  <span className="flex size-3 rounded-full bg-accent animate-pulse" />
                  <span className="text-xs uppercase font-bold tracking-wider text-accent">
                    Live Dispatch Available Now
                  </span>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold font-default">
                  Serving All Jonesboro Neighborhoods
                </h3>
                <p className="text-sm text-primary-foreground/90 mt-1 max-w-md">
                  On-call mobile units with specialized sewer, pipe, and water heater equipment ready for dispatch.
                </p>
              </div>
            </div>

            {/* Quick Stats Grid */}
            <div className="grid grid-cols-2 gap-4">
              <Card className="border border-border/60 bg-card shadow-xs">
                <CardContent className="p-4 sm:p-5 flex items-center gap-3">
                  <div className="p-2.5 rounded-lg bg-primary/10 text-primary">
                    <Clock className="size-5" />
                  </div>
                  <div>
                    <div className="text-xl sm:text-2xl font-bold text-foreground font-default">
                      {"< 45 Min"}
                    </div>
                    <div className="text-xs text-muted-foreground font-medium">
                      Average Local Arrival
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border border-border/60 bg-card shadow-xs">
                <CardContent className="p-4 sm:p-5 flex items-center gap-3">
                  <div className="p-2.5 rounded-lg bg-accent/20 text-accent-foreground">
                    <Sparkles className="size-5 text-accent" />
                  </div>
                  <div>
                    <div className="text-xl sm:text-2xl font-bold text-foreground font-default">
                      24/7/365
                    </div>
                    <div className="text-xs text-muted-foreground font-medium">
                      Holidays & Weekends
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </motion.div>

          {/* Right Column: Key Coverage Zones & Zip Codes */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInVariants} className="lg:col-span-6 flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg sm:text-xl font-bold text-foreground font-default">
                Jonesboro Neighborhoods & Zip Codes
              </h3>
              <Badge variant="outline" className="text-xs">
                Clayton County, GA
              </Badge>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
              {coverageZones.map((zone, i) => <div key={zone.name} data-index={i} className="p-4 rounded-xl border border-border bg-card/80 hover:bg-card hover:border-primary/40 transition-colors shadow-2xs flex flex-col justify-between gap-2">
                  <div>
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <span className="font-semibold text-sm text-foreground font-default">
                        {zone.name}
                      </span>
                      <span className="text-xs font-mono font-medium px-2 py-0.5 rounded-md bg-secondary text-secondary-foreground shrink-0">
                        {zone.zip}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-snug">
                      {zone.detail}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-primary font-medium pt-1">
                    <CheckCircle2 className="size-3.5 text-chart-4" />
                    <span>Active 24/7 Service</span>
                  </div>
                </div>)}
            </div>

            {/* Common Emergency Scenarios Handled in Jonesboro */}
            <div className="p-4 sm:p-5 rounded-xl bg-secondary/50 border border-border mt-2">
              <div className="flex items-center gap-2 mb-2">
                <Navigation className="size-4 text-primary" />
                <span className="text-xs uppercase font-bold tracking-wider text-foreground">
                  Need Help Outside Jonesboro City Limits?
                </span>
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground mb-3 leading-relaxed">
                We also provide prompt emergency coverage to surrounding communities including Morrow, Riverdale, Forest Park, Stockbridge, and Lovejoy.
              </p>
              <div className="flex flex-wrap gap-2">
                <Badge variant="secondary" className="text-xs bg-background/80 border border-border">
                  Morrow, GA (30260)
                </Badge>
                <Badge variant="secondary" className="text-xs bg-background/80 border border-border">
                  Riverdale, GA (30274)
                </Badge>
                <Badge variant="secondary" className="text-xs bg-background/80 border border-border">
                  Lovejoy, GA (30250)
                </Badge>
                <Badge variant="secondary" className="text-xs bg-background/80 border border-border">
                  Forest Park, GA (30297)
                </Badge>
              </div>
            </div>
          </motion.div>
        </div>

        <Separator className="my-10" />

        {/* 3 Value Pillars */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {dispatchHighlights.map((item, i) => {
          const Icon = item.icon;
          return <div key={item.title} data-index={i} className="flex items-start gap-4 p-5 rounded-xl border border-border/70 bg-card/60 shadow-2xs">
                <div className="p-3 rounded-lg bg-primary text-primary-foreground shrink-0 shadow-xs">
                  <Icon className="size-5" />
                </div>
                <div>
                  <h4 className="font-semibold text-base text-foreground font-default mb-1">
                    {item.title}
                  </h4>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </div>;
        })}
        </div>

        {/* Emergency Call Banner / Immediate Action */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeInVariants} className="rounded-2xl bg-primary text-primary-foreground p-6 sm:p-10 shadow-xl relative overflow-hidden flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="max-w-xl text-center md:text-left">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 text-xs font-semibold text-primary-foreground mb-3">
              <span className="size-2 rounded-full bg-accent animate-ping" />
              Jonesboro Dispatch Center On Standby
            </span>
            <h3 className="text-2xl sm:text-3xl font-bold font-default text-primary-foreground">
              Have a Plumbing Emergency in Jonesboro Right Now?
            </h3>
            <p className="mt-2 text-sm text-primary-foreground/80 leading-relaxed">
              Do not let standing water or a collapsed line damage your property. Speak directly to our local master plumber on duty.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-3.5 w-full md:w-auto shrink-0">
            <Button asChild size="lg" className="w-full sm:w-auto bg-accent text-accent-foreground hover:bg-accent/90 font-bold px-7 h-12 shadow-md">
              <a href="tel:4708479076" className="inline-flex items-center justify-center gap-2">
                <Phone className="size-4" />
                Call (470) 847-9076
              </a>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto border-white/30 text-primary-foreground hover:bg-white/10 h-12 px-6">
              <Link to="/contact/">Request Online Dispatch</Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>;
}